import React, { ReactElement, useCallback, useEffect } from 'react';
import { Paper, Container, Box, Typography, Checkbox, MenuItem, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import useLessonAssignment from 'state/providers/LessonAssignmentProvider';
import MultiSelectDropdown from 'components/@common/Inputs/MultiSelectDropdown';
import { translate } from 'translations/translator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { parseQueryParams } from 'util/query-params-util';
import { useLocation } from 'react-router-dom';
import { AssignmentEngineParams } from 'state/providers/LessonAssignmentProvider/types';
import LessonAssignmentTranslationVariant from 'translations/variants/lessonAssignment';
const {
  allStudents: allStudentsText,
  pleaseSelectClassroom,
  pleaseSelectClassroomFirst,
  selectClassesAndStudents,
  selectTheClassesTitle
} = LessonAssignmentTranslationVariant;

const getTranslation = key => translate('lessonAssignment', key);

const LessonAssignmentStepTwo = () => {
  const [
    { stepTwoFormState, lessonAssignmentState, stepOneFormState /* stepperState */ },
    { lessonAssignmentDispatch, stepTwoFormDispatch }
  ] = useLessonAssignment();
  const classes = useStyles();
  const location = useLocation();

  useEffect(() => {
    const queryParams = parseQueryParams(location.search);
    if (queryParams?.assignment === AssignmentEngineParams.EDIT) {
      const editAssignmentData = Object.values(stepOneFormState.form) as any[];

      for (const { lesson } of editAssignmentData) {
        stepTwoFormDispatch.setPreselectedClassroomsAndStudents(
          [lesson.classroomId],
          lesson?.studentIds ?? []
        );
      }
    }
    // eslint-disable-next-line
  }, []);

  const getStudents = (classroomIds: any = []) =>
    lessonAssignmentState?.students?.reduce((acc, student: any) => {
      if (!classroomIds.includes(student.value.classroomId)) return acc;
      acc.push(student);
      return acc;
    }, []);

  const multiSelectCheckBox =
    (formKey: 'studentIds' | 'classroomIds') =>
    (options: any = [], id = 'id') => {
      return options.map((option, idx): ReactElement => {
        return (
          <MenuItem key={`${idx}-dropdown-${id}-item`} value={option.value[id]}>
            <Checkbox checked={stepTwoFormState.form?.[formKey].indexOf(option.value.id) > -1} />
            <ListItemText primary={option.label} />
          </MenuItem>
        );
      });
    };

  const handleRemove = (formKey, id) => () => {
    const filtered = stepTwoFormState.form?.[formKey].filter(x => x !== id);
    stepTwoFormDispatch.onMultiSelectFormChange(formKey, filtered);
  };

  const getSelectedClassrooms = useCallback(
    (formKey): any =>
      lessonAssignmentState?.classrooms?.filter(x =>
        stepTwoFormState.form?.[formKey].includes(x.value.id)
      ),
    // eslint-disable-next-line
    [stepTwoFormState.form]
  );

  const getSelectedStudents = useCallback(
    formKey =>
      lessonAssignmentState?.students?.filter(x =>
        stepTwoFormState.form?.[formKey].includes(x.value.id)
      ),
    // eslint-disable-next-line
    [stepTwoFormState.form]
  );

  useEffect(() => {
    const selectedClassrooms = getSelectedClassrooms('classroomIds');
    const selectedStudents = getSelectedStudents('studentIds');
    const classroomsWithStudents = selectedClassrooms.reduce((acc, classroom) => {
      const students = selectedStudents?.filter(
        ({ value }) => value.classroomId === classroom.value.id
      );
      acc.push({
        ...classroom,
        ...(students?.length && { students })
      });
      return acc;
    }, []);
    lessonAssignmentDispatch.setSelectedClassroomsAndStudents(classroomsWithStudents);
    // eslint-disable-next-line
  }, [stepTwoFormState.form, getSelectedStudents, getSelectedClassrooms]);

  const Chip = ({ label, handleRemove }) => (
    <Box className={classes.chip}>
      <Typography style={{ color: '#fff' }}>
        {label}{' '}
        <FontAwesomeIcon
          icon={faTimes}
          fontSize="14"
          className={classes.icon}
          onClick={handleRemove}
        />
      </Typography>
    </Box>
  );

  return (
    <Paper style={{ margin: '32px 40px' }}>
      <Container className={classes.container}>
        <Box className={classes.headerClass}>
          <Typography variant="h1">{getTranslation(selectClassesAndStudents)}</Typography>
          <Typography variant="subtitle1">{getTranslation(selectTheClassesTitle)}</Typography>
        </Box>
        <MultiSelectDropdown
          name={'classroomIds'}
          label={getTranslation('selectClassroom')}
          value={stepTwoFormState.form?.classroomIds ?? []}
          onChange={stepTwoFormDispatch.onMultiSelectFormChange}
          getRenderOptions={multiSelectCheckBox('classroomIds')}
          options={lessonAssignmentState?.classrooms as any}
          inputProps={{ className: classes.input }}
          labelProps={{ className: classes.label }}
          className={classes.dropdown}
          displayEmpty
          getRenderValue={
            !stepTwoFormState.form?.classroomIds.length
              ? () => getTranslation(pleaseSelectClassroom)
              : undefined
          }
        />
        <Box className={classes.chipContainer}>
          {getSelectedClassrooms('classroomIds').map((x, idx) => (
            <Chip
              key={`classroom-chip-${idx}`}
              label={x.label}
              handleRemove={handleRemove('classroomIds', x.value.id)}
            />
          ))}
        </Box>
        <div style={{ marginTop: '30px' }}>
          <MultiSelectDropdown
            name={'studentIds'}
            label={getTranslation('selectStudents')}
            value={stepTwoFormState.form?.studentIds ?? []}
            onChange={stepTwoFormDispatch.onMultiSelectFormChange}
            getRenderOptions={multiSelectCheckBox('studentIds')}
            options={getStudents(stepTwoFormState.form?.classroomIds as any)}
            inputProps={{ className: classes.input, style: { marginTop: '30px' } }}
            labelProps={{ className: classes.label }}
            className={classes.dropdown}
            displayEmpty
            disabled={!stepTwoFormState.form?.classroomIds.length}
            getRenderValue={
              !stepTwoFormState.form?.classroomIds?.length
                ? () => getTranslation(pleaseSelectClassroomFirst)
                : !stepTwoFormState.form?.studentIds?.length
                ? () => getTranslation(allStudentsText)
                : undefined
            }
          />
        </div>

        <Box className={classes.chipContainer}>
          {getSelectedStudents('studentIds')?.map((x, idx) => {
            return (
              <Chip
                key={`students-chip-${idx}`}
                label={x.label}
                handleRemove={handleRemove('studentIds', x.value.id)}
              />
            );
          })}
        </Box>
      </Container>
    </Paper>
  );
};

export default LessonAssignmentStepTwo;

const adjustment = 281;
const useStyles = makeStyles(() => ({
  container: {
    display: 'flex !important',
    flex: '0.7 !important',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: `calc(100vh - ${adjustment}px)`
  },
  headerClass: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    margin: '32px'
  },
  dropdown: {
    margin: '15px 0px'
  },
  input: {
    width: '616px'
  },
  label: {
    fontSize: '14px',
    color: 'black !important',
    textTransform: 'none !important',
    top: '-25px !important'
  } as any,
  chipContainer: { display: 'flex', flexFlow: 'wrap', width: '645px', marginBottom: '15px' },
  chip: {
    backgroundColor: '#2878C1',
    padding: '1px 10px 1px 12px',
    border: '20px',
    borderRadius: '13px',
    margin: '0px 10px 10px 0px'
  },
  icon: {
    marginLeft: 5,
    '&:hover': {
      fontSize: '15px',
      cursor: 'pointer'
    }
  }
}));
